import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  AssociateSetupContext,
  AuthenticationContext,
  IntroModule,
  OnboardingSteps,
  useCompleteAssociateOnboardingStep,
  useGetAssociateIntroModules,
} from '@vs-online-only-app/state';

type IntroModulesState = {
  currentIntroModule?: IntroModule;
  isLoading: boolean;
  goToNextModule: () => void;
  goToPreviousModule: () => void;
  completeIntroModules: () => Promise<void>;
  nrOfModules: number;
  currentModuleIndex: number;
  canGoToPreviousModule: boolean;
  isOnLastModule: boolean;
  error: boolean;
};

const IntroModulesContext = createContext<IntroModulesState>(
  {} as IntroModulesState,
);

const IntroModulesProvider: FC<PropsWithChildren> = (props) => {
  const [currentModuleIndex, setCurrentModuleIndex] = useState(0);

  const { data, isLoading, error } = useGetAssociateIntroModules();
  const completeAssociateOnboardingStep = useCompleteAssociateOnboardingStep();
  const { refetchUser } = useContext(AuthenticationContext);
  const { refetchSetupState } = useContext(AssociateSetupContext);

  const introModules: IntroModule[] = useMemo(() => {
    if (!data?.introModules) return [];
    return data.introModules;
  }, [data]);

  const goToNextModule = useCallback(() => {
    if (currentModuleIndex === introModules.length - 1) return;
    setCurrentModuleIndex((prev) => prev + 1);
  }, [currentModuleIndex, introModules.length]);

  const goToPreviousModule = useCallback(() => {
    if (currentModuleIndex === 0) return;
    setCurrentModuleIndex((prev) => prev - 1);
  }, [currentModuleIndex]);

  const completeIntroModules = useCallback(async () => {
    // Wee need to complete both the introduction and the introductionComplete steps
    // since there are no onboarding demo in the web app
    await completeAssociateOnboardingStep.mutateAsync({
      onboardingStep: OnboardingSteps.introduction,
    });
    await completeAssociateOnboardingStep.mutateAsync({
      onboardingStep: OnboardingSteps.introductionComplete,
    });
    await Promise.all([refetchSetupState(), refetchUser()]);
  }, [completeAssociateOnboardingStep, refetchSetupState, refetchUser]);

  const value: IntroModulesState = useMemo(
    () => ({
      currentIntroModule: introModules[currentModuleIndex],
      isLoading,
      goToNextModule,
      goToPreviousModule,
      completeIntroModules,
      nrOfModules: introModules.length,
      currentModuleIndex,
      canGoToPreviousModule: currentModuleIndex > 0,
      isOnLastModule: currentModuleIndex === introModules.length - 1,
      error: Boolean(error),
    }),
    [
      completeIntroModules,
      currentModuleIndex,
      goToNextModule,
      goToPreviousModule,
      introModules,
      isLoading,
      error,
    ],
  );

  return (
    <IntroModulesContext.Provider value={value}>
      {props.children}
    </IntroModulesContext.Provider>
  );
};

export { IntroModulesProvider, IntroModulesContext };
